/**
 * ✅ Consolidated Vehicle Detail Page Schemas
 * 
 * Replaces 4 overlapping hooks with a single unified implementation:
 * - useVehicleDetailProductSchemas (Product + Car + Vehicle + PriceSpec + MerchantReturn)
 * - useVehicleEcommerceSchemas (Enhanced e-commerce schemas)
 * - useVehicleDetailSchema (Legacy Vehicle + Product + Car + FAQ + Breadcrumb)
 * - useCarForSaleSchema (CarForSale schema)
 * 
 * This hook injects ALL required schemas for VDP in one place with no duplication.
 * 
 * ── Schema ID Map (VDP) ──────────────────────────────────────────────────────
 * • vdp-schema-product      → Product with @id: /inventory/{uuid}#product
 * • vdp-schema-car          → Car with @id: /inventory/{uuid}#car
 * • vdp-schema-webpage      → WebPage with @id: /inventory/{uuid}#webpage
 * • vdp-schema-breadcrumb   → BreadcrumbList with @id: /inventory/{uuid}#breadcrumb
 * • vdp-schema-faq          → FAQPage with @id: /inventory/{uuid}#faq
 * 
 * ── @id Collision Check ──────────────────────────────────────────────────────
 * ✅ Homepage featured vehicles use @id: /inventory/{uuid}#car (different page = safe)
 * ✅ Inventory AggregateOffer uses @id: /inventory#aggregate-offer (different fragment)
 * ✅ Homepage AggregateOffer uses @id: /#aggregate-offer (different path)
 */

import { useEffect } from 'react';
import { type Vehicle } from './useVehicles';
import { CORE_BUSINESS } from '../lib/coreSchemas';
import { injectJsonLd, removeJsonLd } from '../lib/schemaBuilder';

interface AggregateRating {
  ratingValue: string | number;
  reviewCount: number;
}

interface Review {
  name: string;
  rating: number;
  text: string;
  created_at: string;
}

/**
 * Consolidated VDP Schema Hook
 * Injects all required schemas for vehicle detail pages with no duplication
 */
export function useVehicleDetailSchemas(
  vehicle: Vehicle | undefined,
  aggregateRating?: AggregateRating,
  reviews?: Review[]
) {
  useEffect(() => {
    if (!vehicle) return;

    const vehicleUrl = `${CORE_BUSINESS.url}/inventory/${vehicle.uuid || vehicle.id}`;
    const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ' ' + vehicle.trim : ''}`;
    const image = vehicle.image || '';
    const images = vehicle.images && vehicle.images.length > 0 ? vehicle.images : [image].filter(Boolean);

    const validFrom = new Date().toISOString().split('T')[0];
    const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const availability =
      vehicle.status === 'available'
        ? 'https://schema.org/InStock'
        : vehicle.status === 'pending'
        ? 'https://schema.org/LimitedAvailability'
        : 'https://schema.org/SoldOut';

    // ── Build Offer (shared across all schemas) ──────────────────────────────
    const buildOffer = (offerId: string) => ({
      '@type': 'Offer',
      '@id': `${vehicleUrl}#${offerId}`,
      url: vehicleUrl,
      priceCurrency: 'USD',
      price: vehicle.price,
      validFrom,
      priceValidUntil,
      itemCondition: 'https://schema.org/UsedCondition',
      availability,
      seller: {
        '@type': 'AutoDealer',
        '@id': `${CORE_BUSINESS.url}/#organization`,
        name: CORE_BUSINESS.name,
        url: CORE_BUSINESS.url,
        telephone: CORE_BUSINESS.telephone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: CORE_BUSINESS.address.streetAddress,
          addressLocality: CORE_BUSINESS.address.addressLocality,
          addressRegion: CORE_BUSINESS.address.addressRegion,
          postalCode: CORE_BUSINESS.address.postalCode,
          addressCountry: CORE_BUSINESS.address.addressCountry,
        },
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
          addressRegion: ['OR', 'WA'],
        },
        shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'USD' },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 5, unitCode: 'DAY' },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'US',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
      },
    });

    // ── Build AggregateRating (shared) ────────────────────────────────────────
    const buildAggregateRating = () =>
      aggregateRating && aggregateRating.reviewCount > 0
        ? {
            '@type': 'AggregateRating',
            ratingValue: String(aggregateRating.ratingValue),
            reviewCount: aggregateRating.reviewCount,
            bestRating: '5',
            worstRating: '1',
          }
        : undefined;

    // ── Build Reviews (shared) ────────────────────────────────────────────────
    const buildReviews = () =>
      reviews && reviews.length > 0
        ? reviews.slice(0, 3).map((r) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name },
            datePublished: new Date(r.created_at).toISOString().split('T')[0],
            reviewBody: r.text,
            reviewRating: {
              '@type': 'Rating',
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
          }))
        : undefined;

    // ── 1. Product Schema (Google Shopping) ───────────────────────────────────
    const productSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${vehicleUrl}#product`,
      name: vehicleName,
      description:
        vehicle.description ||
        `${vehicleName} with ${vehicle.mileage.toLocaleString()} miles. ${vehicle.transmission || ''} transmission, ${vehicle.drivetrain || ''} drivetrain, ${vehicle.exteriorColor || ''} exterior. Available at Rockhound Auto in Warrenton, Oregon.`,
      image: images,
      brand: { '@type': 'Brand', name: vehicle.make },
      ...(vehicle.vin ? { sku: vehicle.vin, mpn: vehicle.vin, gtin: vehicle.vin } : { sku: vehicle.uuid || String(vehicle.id) }),
      itemCondition: 'https://schema.org/UsedCondition',
      url: vehicleUrl,
      category: `Used ${vehicle.bodyType || 'Vehicle'}`,
      offers: buildOffer('product-offer'),
    };

    const aggRating = buildAggregateRating();
    if (aggRating) productSchema.aggregateRating = aggRating;

    const reviewsData = buildReviews();
    if (reviewsData) productSchema.review = reviewsData;

    injectJsonLd('vdp-schema-product', productSchema);

    // ── 2. Car Schema (Automotive-specific) ───────────────────────────────────
    const carSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Car',
      '@id': `${vehicleUrl}#car`,
      name: vehicleName,
      description:
        vehicle.description ||
        `${vehicleName} with ${vehicle.mileage.toLocaleString()} miles. ${vehicle.transmission || ''} transmission, ${vehicle.drivetrain || ''} drivetrain. Available at Rockhound Auto in Warrenton, Oregon.`,
      image: images,
      brand: { '@type': 'Brand', name: vehicle.make },
      model: vehicle.model,
      vehicleModelDate: String(vehicle.year),
      ...(vehicle.vin ? { vehicleIdentificationNumber: vehicle.vin } : {}),
      mileageFromOdometer: { '@type': 'QuantitativeValue', value: vehicle.mileage, unitCode: 'SMI' },
      ...(vehicle.bodyType ? { bodyType: vehicle.bodyType } : {}),
      ...(vehicle.fuelType ? { fuelType: vehicle.fuelType } : {}),
      ...(vehicle.transmission ? { vehicleTransmission: vehicle.transmission } : {}),
      ...(vehicle.drivetrain ? { driveWheelConfiguration: vehicle.drivetrain } : {}),
      ...(vehicle.exteriorColor ? { color: vehicle.exteriorColor } : {}),
      ...(vehicle.interiorColor ? { vehicleInteriorColor: vehicle.interiorColor } : {}),
      ...(vehicle.engineSize ? { vehicleEngine: { '@type': 'EngineSpecification', name: vehicle.engineSize } } : {}),
      ...(vehicle.doors ? { numberOfDoors: vehicle.doors } : {}),
      ...(vehicle.seating_capacity ? { vehicleSeatingCapacity: vehicle.seating_capacity } : {}),
      ...(vehicle.mpgCity && vehicle.mpgHighway
        ? { fuelEfficiency: { '@type': 'QuantitativeValue', value: `${vehicle.mpgCity}/${vehicle.mpgHighway}`, unitText: 'MPG city/highway' } }
        : {}),
      itemCondition: 'https://schema.org/UsedCondition',
      url: vehicleUrl,
      offers: buildOffer('car-offer'),
    };

    if (aggRating) carSchema.aggregateRating = aggRating;
    if (reviewsData) carSchema.review = reviewsData;

    injectJsonLd('vdp-schema-car', carSchema);

    // ── 3. WebPage Schema (Google recommends for all pages) ───────────────────
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${vehicleUrl}#webpage`,
      url: vehicleUrl,
      name: `${vehicleName} for Sale | Rockhound Auto`,
      description:
        vehicle.description ||
        `${vehicleName} with ${vehicle.mileage.toLocaleString()} miles. ${vehicle.transmission || ''} transmission, ${vehicle.drivetrain || ''} drivetrain. Available at Rockhound Auto in Warrenton, Oregon.`,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${CORE_BUSINESS.url}/#website`,
        url: CORE_BUSINESS.url,
        name: CORE_BUSINESS.name,
      },
      breadcrumb: {
        '@id': `${vehicleUrl}#breadcrumb`,
      },
      mainEntity: {
        '@id': `${vehicleUrl}#product`,
      },
      publisher: {
        '@type': 'AutoDealer',
        '@id': `${CORE_BUSINESS.url}/#organization`,
        name: CORE_BUSINESS.name,
      },
    };

    injectJsonLd('vdp-schema-webpage', webPageSchema);

    // ── 4. BreadcrumbList Schema ──────────────────────────────────────────────
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${vehicleUrl}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: CORE_BUSINESS.url },
        { '@type': 'ListItem', position: 2, name: 'Inventory', item: `${CORE_BUSINESS.url}/inventory` },
        { '@type': 'ListItem', position: 3, name: vehicleName, item: vehicleUrl },
      ],
    };

    injectJsonLd('vdp-schema-breadcrumb', breadcrumbSchema);

    // ── 5. FAQPage Schema ─────────────────────────────────────────────────────
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${vehicleUrl}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: `What is the price of the ${vehicleName}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `The ${vehicleName} is listed at $${vehicle.price.toLocaleString()} at Rockhound Auto in Warrenton, Oregon. Financing options are available for all credit types.`,
          },
        },
        {
          '@type': 'Question',
          name: `How many miles does the ${vehicleName} have?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `This ${vehicleName} has ${vehicle.mileage.toLocaleString()} miles on the odometer.`,
          },
        },
        {
          '@type': 'Question',
          name: `Can I schedule a test drive for the ${vehicleName}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes! You can schedule a test drive for the ${vehicleName} by contacting Rockhound Auto at (855) 468-6348 or using the online form on our website. We also offer delivery to Astoria, Seaside, Tillamook, and throughout the Pacific Northwest.`,
          },
        },
        {
          '@type': 'Question',
          name: `Does Rockhound Auto offer financing for the ${vehicleName}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes, Rockhound Auto offers flexible financing for the ${vehicleName}. We work with multiple lenders for all credit situations including bad credit and first-time buyers. Visit our financing page or contact us to get pre-approved.`,
          },
        },
        ...(vehicle.vin
          ? [
              {
                '@type': 'Question',
                name: `What is the VIN for the ${vehicleName}?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `The VIN for this ${vehicleName} is ${vehicle.vin}. A full vehicle history report is available upon request.`,
                },
              },
            ]
          : []),
      ],
    };

    injectJsonLd('vdp-schema-faq', faqSchema);

    // ── 6. SpeakableSpecification Schema (Voice Search / Google Assistant) ────
    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${vehicleUrl}#speakable-webpage`,
      url: vehicleUrl,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: [
          '.vdp-vehicle-name',
          '.vdp-price',
          '.vdp-mileage',
          '.vdp-availability',
          '.vdp-drivetrain',
          '.vdp-transmission',
          '.vdp-speakable-summary',
        ],
        xpath: [
          "//*[contains(@class,'vdp-vehicle-name')]",
          "//*[contains(@class,'vdp-price')]",
          "//*[contains(@class,'vdp-mileage')]",
          "//*[contains(@class,'vdp-availability')]",
        ],
      },
    };

    injectJsonLd('vdp-schema-speakable', speakableSchema);

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      removeJsonLd('vdp-schema-product');
      removeJsonLd('vdp-schema-car');
      removeJsonLd('vdp-schema-webpage');
      removeJsonLd('vdp-schema-breadcrumb');
      removeJsonLd('vdp-schema-faq');
      removeJsonLd('vdp-schema-speakable');
    };
  }, [vehicle, aggregateRating, reviews]);
}